import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { shouldSubmitSearch } from '../searchSubmit';
import { HeaderMoreMenu } from './HeaderMoreMenu';
import { LogoMark } from './LogoMark';
import { SearchField } from './SearchField';
import { SiteFooter } from './SiteFooter';
import { PageHead } from './PageHead';
import './SearchResultsPage.css';
import './AboutPage.css';

export function AddStorePage() {
  const navigate = useNavigate();
  const [draftQuery, setDraftQuery] = useState('');

  function applySearch(query) {
    const trimmed = typeof query === 'string' ? query.trim() : '';
    if (!shouldSubmitSearch(trimmed)) {
      return;
    }
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  }

  return (
    <div className="results-page">
      <PageHead
        title="List your Malta online store | Shops.mt"
        description="Add your Maltese online store to Shops.mt. Reach shoppers searching with intent and send qualified traffic to your own checkout."
        canonicalPath="/add-store"
      />
      <header className="results-page__header">
        <div className="results-page__header-inner">
          <div className="results-page__header-left">
            <LogoMark variant="header" />
            <HeaderMoreMenu />
          </div>
          <div className="results-page__header-center">
            <SearchField
              id="add-store-search"
              className="results-page__search search-bar"
              value={draftQuery}
              onChange={setDraftQuery}
              onSubmitSearch={applySearch}
              placeholder="Search products…"
              gaSearchLocation="add_store"
            />
          </div>
          <nav
            className="results-page__header-nav"
            aria-label="Site sections"
          >
            <Link className="results-page__nav-link" to="/about">
              About
            </Link>
            <Link
              className="results-page__nav-link results-page__nav-link--current"
              to="/add-store"
              aria-current="page"
            >
              Add your store
            </Link>
          </nav>
        </div>
      </header>

      <div className="about-page about-page--store">
        <section
          className="about-page__section about-page__shop"
          aria-labelledby="shop-heading"
        >
          <h1 id="shop-heading" className="about-page__title">
            Malta&apos;s first product search engine — built to send buyers to
            your store
          </h1>
          <div className="about-page__prose">
            <p>
              Shoppers on Shops.mt aren&apos;t browsing — they&apos;re searching
              with intent, ready to buy. We connect high-intent, purchase-ready
              visitors directly to Maltese online stores, so you get traffic that
              converts, not clicks that bounce.
            </p>
            <p className="about-page__cta-line">
              Many stores are already listed for free. We&apos;re building extra
              tools for shop owners — more on that soon.
            </p>
          </div>

          <div
            className="about-page__packages"
            aria-label="Shops.mt listing packages"
          >
            <article
              id="pro-package"
              className="about-page__package about-page__package--pro"
            >
              <div className="about-page__package-head">
                <h3 className="about-page__package-name">Pro</h3>
                <span className="about-page__package-ribbon">Coming soon</span>
              </div>
              <p className="about-page__package-lede">
                We&apos;re working on extra tools for store owners — branding,
                reviews, analytics, and more ways to stand out on Shops.mt.
                Pro isn&apos;t available yet, but it&apos;s on the way.
              </p>
              <p className="about-page__package-footnote">
                Want to hear when Pro launches? Email{' '}
                <a href="mailto:info@shops.mt">info@shops.mt</a>.
              </p>
            </article>

            <article className="about-page__package about-page__package--listed">
              <div className="about-page__package-head">
                <h3 className="about-page__package-name">Listed</h3>
                <span className="about-page__package-badge">Free</span>
              </div>
              <p className="about-page__package-lede">
                Your store is already in our index. Products are discoverable by
                shoppers across Malta.
              </p>
              <ul className="about-page__package-features">
                <li>
                  <span aria-hidden="true">🔍</span>
                  Discoverable in search results
                </li>
                <li>
                  <span aria-hidden="true">🕐</span>
                  Products updated on standard crawl schedule
                </li>
                <li>
                  <span aria-hidden="true">📋</span>
                  Basic store profile (no logo)
                </li>
                <li>
                  <span aria-hidden="true">💬</span>
                  <span>
                    Customer reviews displayed (view only)
                    <span className="about-page__feature-badge">Coming soon</span>
                  </span>
                </li>
              </ul>
            </article>
          </div>

          <aside
            className="about-page__apply-banner"
            aria-labelledby="apply-banner-heading"
          >
            <h3 id="apply-banner-heading" className="about-page__apply-banner-title">
              Not listed yet? Apply to join.
            </h3>
            <p className="about-page__apply-banner-body">
              If your store isn&apos;t in our index yet, tell us about your shop
              and we&apos;ll review your application.
            </p>
            <Link
              className="about-page__package-cta about-page__package-cta--secondary"
              to="/add-store/apply"
            >
              Apply to join
            </Link>
          </aside>

          <aside
            className="about-page__delist-notice"
            aria-labelledby="delist-notice-heading"
          >
            <h3
              id="delist-notice-heading"
              className="about-page__delist-notice-title"
            >
              Already listed and prefer not to be?
            </h3>
            <p className="about-page__delist-notice-body">
              No problem — if you&apos;d rather your store wasn&apos;t on
              Shops.mt, email{' '}
              <a href="mailto:info@shops.mt">info@shops.mt</a> and we&apos;ll
              remove it promptly.
            </p>
          </aside>

          {/* Replace placeholder logos with real shop logos once available
          <div
            className="about-page__social-proof"
            aria-label="Trusted by stores across Malta"
          >
            <p className="about-page__social-proof-label">
              Trusted by stores across Malta
            </p>
            <ul className="about-page__social-proof-logos">
              {Array.from({ length: 6 }, (_, i) => (
                <li key={i}>
                  <span
                    className="about-page__social-proof-logo"
                    aria-hidden="true"
                  />
                </li>
              ))}
            </ul>
          </div>
          */}
        </section>
      </div>
      <SiteFooter />
    </div>
  );
}
